/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

// TestIDs are categorize by 3 ways, Pages, Features, and Components

// Pages are TestIDs for the whole page

// Features are TestIDs that is primarily used on the feature,
// The components would not exist without the feature, such as agreement, order, product,

// Components are TestIDs that is primarily used on reused component that are used
// throughout different features, such as the header, footer, etc.

// * Only three levels of TestIDs are allowed
// * "Pages", "Feat", "Components" are the only items are allowed on root level

// For Pages
// the first level should be the page name.
//    - Example: "feat.agreement.tabview.pastAgreements"
//

// For Features
// the first level should be the feature name, and
// the second level should be the group name.
// the thrid level should be the element
//    - Example: "feat.agreement.tabview.pastAgreements"
//

// For Components
// the first level should be the component name, and
// the 2nd level should be the element.
//    - Example: "component.contactCard.storeName"
//

export const TestIds = {
  pages: {
    pastProducts: 'past-products-page',
    productDetails: 'product-details-page',
    productNotFound: 'product-not-found',
    cart: 'cart-page',
    salesAgreements: 'sales-agreements-home',
    salesAgreement: 'sales-agreements-details',
    activities: 'activities-page',
    orders: 'orders-page',
    orderDetails: 'order-details',
    quotes: 'quotes-page',
    quoteDetails: 'quote-details',
    quoteOrderSubmitted: 'quote-order-submitted',
    requestAQuoteSuccess: 'request-a-quote-success',
    home: 'home-page',
    drawdownSuccess: 'drawdown-success',
    campaignSuccess: 'campaign-success',
    noShareHolderAccess: 'not-a-shareholder',
    mixedAccessShareholderAccess: 'mixed-access',
    mainScrollContainer: 'main-scroll-container',
    maintenancePage: 'maitenance-page',
    seedOrder: 'seed-order',
    seedOrderSummary: 'seed-order-summary',
    seedOrderSuccess: 'seed-order-success',
    versionMismatch: 'version-mismatch-page',
    ceLinkNoAccess: 'ce-link-no-access',
    ceLink: 'ce-link',
  },
  feat: {
    account: {
      userAccount: {
        mobile: {
          container: 'user-account__mobile-container',
        },
        accountMenu: {
          component: 'user-account__account-menu',
        },
        appVersion: {
          component: 'user-account__app-version',
        },
        contactProfileHeader: {
          component: 'user-account__contact-profile-header',
          contactName: 'contact-profile-header__contact-name',
          email: 'contact-profile-header__email',
        },
        featureLinks: {
          component: 'user-account__feature-links',
          accountDetails: 'feature-links__account-details',
          quotes: 'feature-links__quotes',
          salesAgreements: 'feature-links__sales-agreements',
          activities: 'feature-links__activities',
          orders: 'feature-links__orders',
          declarations: 'feature-links__declarations',
          logout: 'feature-links__logout',
        },
        initialsCircle: {
          component: 'user-account__initials',
          button: 'initials__button',
        },
        selectedAccount: {
          component: 'user-account__selected-account',
          accountName: 'selected-account__account-name',
          switchAccount: 'selected-account__switch-account',
          switchAccountModal: 'selected-account__switch-account-modal',
        },
        userAgreements: {
          component: 'user-account__user-agreements',
          privacyLink: 'user-agreements__privacy-policy-link',
          termsLink: 'user-agreements__terms-and-conditions-link',
        },
      },
      profile: {
        mobile: {
          container: 'profile__mobile-container',
        },
        web: {
          container: 'profile__web-container',
        },
        accountActions: {
          component: 'user-account__account-actions',
          myFarmlands: {
            container: 'account-actions__my-farmlands',
            loginMyFarmlands: 'my-farmlands__login-my-farmlands',
          },
          deleteAccount: {
            container: 'account-actions__delete-account',
            deleteAccountBtn: 'delete-account__delete-account-btn',
          },
        },
        deleteAccountModal: {
          component: 'user-account__delete-account-modal',
          closeBtn: 'delete-account-modal__close-button',
          contactCard: 'delete-account-modal__contact-card',
          confirmation: {
            component: 'delete-account-modal__delete-account-confirmation',
            contactName: 'delete-account-confirmation__contact-name',
            accountEmail: 'delete-account-confirmation__account-email',
            deleteAccountBtn: 'delete-account-confirmation__delete-account-btn',
          },
          error: {
            component: 'delete-account-modal__delete-account-error',
            tryAgainBtn: 'delete-account-modal__try-again-btn',
          },
          loader: 'delete-account-confirmation__loader',
          success: {
            component: 'delete-account-modal__delete-account-success',
            confirmation: 'delete-account-success__confirmation',
          },
        },
        personalDetails: {
          component: 'user-account__personal-details',
          firstName: 'personal-details__first-name',
          lastName: 'personal-details__last-name',
          preferredName: 'personal-details__preferred-name',
          phone: 'personal-details__phone',
          email: 'personal-details__email',
        },
      },
      selectedAccountDetails: {
        mobile: {
          container: 'selected-account-details__mobile-container',
        },
        web: {
          container: 'selected-account-details__web-container',
        },
        accountName: {
          component: 'selected-account-details__account-name',
          name: 'account-name__name',
          legalName: 'account-names__legal-name',
          accountNumber: 'account-names__account-number',
        },
        contactDetails: {
          component: 'selected-account-details__contact-details',
          accountPhone: 'contact-details__account-phone',
          accountEmail: 'contact-details__account-email',
        },
        statements: {
          component: 'selected-account-details__statements',
          statementEmail: 'statements__statement-email',
        },
      },
      navbarAccountMenu: {
        component: 'navbar-account-menu',
        popper: 'navbar-account-menu__popper',
      },
    },
    costPrice: {
      component: 'cost-price__component',
      loading: {
        component: 'cost-price__loading',
        siteContainer: 'cost-price-loading__site-container',
      },
      error: {
        component: 'cost-price__error',
        tryAgainBtn: 'cost-price-error__try-again-btn',
      },
      empty: {
        component: 'cost-price__empty',
      },
      success: {
        component: 'cost-price__success',
        siteContainer: 'cost-price-success__site-container',
        siteBreakpointPrice: 'cost-price-success__site-breakpoint-price',
      },
    },
    splash: {
      loginButton: 'login-button',
      registerButton: 'register-button',
    },
    logout: {
      loader: 'logout-loader',
    },
    logoutCE: {
      loader: 'logout-ce-loader',
    },
    dashboard: {
      list: {
        dashboardHelperMessage: 'dashboard-helperMessage',
        viewAllProducts: 'dashboard-viewAllProducts',
        viewAllAgreements: 'dashboard-viewAllAgreements',
        viewAllOrders: 'dashboard-viewAllorders',
      },
    },
    campaign: {
      nextButton: 'campaign-promo-next-button',
      components: {
        siteSelector: 'campaign-promo-component-site-selector',
      },
      fulfilmentTab: {
        editDelivery: 'campaign-promo-t1-edit-delivery',
        searchInput: 'campaign-promo-t1-search-address-input',
        nameInput: 'campaign-promo-t1-contact-input',
        phoneInput: 'campaign-promo-t1-phone-input',
        dairySupplyInput: 'campaign-promo-t1-dairy-input',
        deliveryNotesInput: 'campaign-promo-t1-delivery-input',
        hiabCheckbox: 'campaign-promo-t1-hiab-checkbox',
      },
      productsTab: {},
      quantityTab: {},
      reviewTab: {
        pickupOrDelivery: 'campaign-promo-t2-pickup-or-delivery',
        deliveryDetailsInput: 'campaign-promo-t2-delivery-details-input',
        orderLocationButton: 'campaign-promo-t2-location-button',
        submitButton: 'campaign-promo-t2-submit-button',
        nameInput: 'campaign-promo-t2-name-input',
        emailInput: 'campaign-promo-t2-email-input',
        phoneInput: 'campaign-promo-t2-phone-input',
        refInput: 'campaign-promo-t2-ref-input',
        splitPay: {
          accNameInput: 'campaign-promo-t2-acc-name-input',
          accNumInput: 'campaign-promo-t2-acc-num-input',
          splitInput: 'campaign-promo-t2-split-input',
          splitResult: 'campaign-promo-t2-split-result',
        },
        deliveryInfo: {
          contactNameInput: 'campaign-promo-t2-deliver-info-contact-name-input',
          phoneNumberInput: 'campaign-promo-t2-deliver-info-phone-input',
          supplyNumberInput: 'campaign-promo-t2-deliver-info-supply-num-input',
          detailsInput: 'campaign-promo-t2-deliver-info-details-input',
          requiredHiabCheckbox: 'campaign-promo-t2-deliver-info-hiad-checkbox',
          radioGroup: 'campaign-promo-t2-deliver-info-radio-group',
          onCharge: 'campaign-promo-t2-deliver-info-radio-on-charge',
          toCustomer: 'campaign-promo-t2-deliver-info-radio-to-customer',
          selectTruck: 'campaign-promo-t2-deliver-info-radio-select-truck',
          selectCarrier: 'campaign-promo-t2-deliver-info-radio-select-carrier',
        },
      },
    },
    agreement: {
      campaignModal: {
        quantityTab: {
          component: 'campaign-promo-quantity-tab__component',
        },
      },
      magnesium: {
        quantityTab: {
          totalUnit: 'agreements-magnesium-quantity-totalunits',
        },
      },
      tabView: {
        pastAgreements: 'past-agreements-tab-button',
        activeAgreements: 'active-agreements-tab-button',
        activeAgreementsList: 'active-sales-agreements-list',
        pastAgreementsList: 'past-sales-agreements-list',
        upcomingAgreementsList: 'upcoming-sales-agreements-list',
      },
      list: {
        noAgreementsMessage: 'noAgreements-message',
        noAgreementsContactBox: 'noAgreements-contact',
      },
      card: {
        agreementCard: 'sales-agreement-card',
        name: 'agreements-Name',
        date: 'agreements-Date',
        timeBadge: 'agreements-TimeBadge',
        fulfillmentBar: 'agreements-Fulfillment',
        product: 'agreements-Products',
      },
      salesOrder: {
        table: 'agreements-sales-order-table',
        tableItem: 'agreements-sales-order-table-item',
      },
      salesLines: {
        container: 'sales-lines-list-container',
        item: 'sales-lines-list-item',
        productDescription: 'line-product-desc',
        agreementTotalLabel: 'line-agreement-total-label',
        agreementTotal: 'line-agreement-total',
        boughtPriceLabel: 'line-bought-price-label',
        boughtPrice: 'line-brought-price',
        remaining: 'line-remaining',
        fulfilled: 'line-fulfilled',
        fulfillmentBar: 'line-fulfillment-bar',
        fulfillmentStatus: 'line-fulfillment-status',
      },
    },
    drawdown: {
      requestButton: 'drawdown-flow-button',
      // Modal
      modal: 'drawdown-modal',
      modalCloseButton: 'drawdown-modal--close',
      continueButton: 'drawdown-continue-button',
      // Drawdown List
      drawdownList: {
        notes: 'drawdown-list-notes',
        errorMessage: 'drawdown-list-errorMessage',
        amountItem: 'drawdown-list-amountItem',
        requiresReviewCheckbox: 'drawdown-list-review-checkbox',
        requiresReviewBanner: 'drawdown-list-required-review-banner',
        overdraftBanner: 'drawdown-list-overdraft-banner',
        // Pass in `productVariantNumber` property
        amountInput: 'drawdown-list-amount',
        amountInputDataId: (id: string) => `drawdown-list-amount-${id}`,
        amountTotalWeightDataId: (id: string) => `drawdown-list-convertedAmount-${id}`,
        amountAvailableDataId: (id: string) => `drawdown-list-availableAmount-${id}`,
      },
      v1: {
        forms: {
          selectedAddressButton: {
            shortAddressButton: 'drawdown-v1-selected-address-button__short-address',
            addAddressButton: 'drawdown-v1-selected-address-button__add-address',
          },
        },
      },
      v2: {
        step2: {
          container: 'drawdown-v2-step2__container',
          title: 'drawdown-v2-step2__title',
          subtitle: 'drawdown-v2-step2__subtitle',
          deliveryDetailsButton: 'drawdown-v2-step2__delivery-details-button',
          freightNote: 'drawdown-v2-step2__freight-note',
          addressErrorMessage: 'drawdown-v2-step2__address-error-message',
          nextButton: 'drawdown-v2-step2__next-button',
        },
      },
    },
    editPrice: {
      modal: 'edit-price-modal',
      unitPrice: 'edit-price-unit-price',
      unitPriceHelperText: 'edit-price-unit-price-helper-text',
      revertChanges: ' edit-price-revert-changes',
      saveChanges: 'edit-price-save-changes',
    },

    product: {
      tabView: {
        pastProductsTab: 'product-past-products-tab',
        pastProductsTabPanel: 'product-past-products-tab-panel',
        allProductsTab: 'product-all-products-tab',
        allProductsTabPanel: 'product-all-products-tab-panel',
      },
      card: {
        chip: 'product-card-chip',
        card: 'product-card',
        alternative: 'product-card-alternative',
        action: 'product-list-product-action',
        name: 'product-list-product-name',
        SKU: 'product-list-product-sku',
        lastOrdered: 'product-list-product-last-ordered',
        quantity: 'product-list-product-quantity',
        size: 'product-list-product-size',
        colour: 'product-list-product-colour',
        image: 'product-list-product-image',
        nationalPrice: 'product-list-product-national-price',
        discountedPrice: 'product-list-product-discounted-price',
        priceNotAvailable: 'product-list-product-price-not-available',
        requestQuoteButton: 'product-request-quote-button',
        orderButton: 'product-order-button',
        showOptionsButton: 'product-show-options-button',
        enquireButton: 'product-enquire-button',
        discountBanner: 'product-discount-banner',
        cartRemoveButton: 'cart-remove-button',
        statusBadge: 'product-card-status-badge',
        editPriceButton: 'product-edit-price-button',
        restoreButton: 'product-restore-button',
      },
      list: {
        clearFilterButton: 'clear-filter-button',
        paginationCounter: 'product-pagination-counter',
        pageNumber: 'product-pageNumber',
        sortingDropdown: 'product-sort-type',
        noProducts: 'no-products',
        noProductsByFilters: 'no-products-by-filters',
      },
      cover: {
        card: 'product-cover-card',
        cardId: (id: string) => `product-cover-card-${id}`,
        title: 'product-cover-title',
        description: 'product-cover-description',
      },
      variant: {
        container: 'product-variant-container',
        sizeDropdown: 'add-variant-size-dropdown',
        sizeDropdownItem: 'add-variant-size-dropdown-item',
        colourDropdown: 'add-variant-colour-dropdown',
        colourDropdownItem: 'add-variant-colour-dropdown-item',
      },
      printVariants: {
        sizeValue: 'print-variant-size-value',
        colourValue: 'print-variant-colour-value',
      },
      details: {
        container: 'product-details-container',
        descriptionContainer: 'product-details-description',
        tabOverview: 'product-details-tab-overview',
        tabProductDetails: 'product-details-tab-details',
        orderButton: 'product-details-order-button',
        enquireButton: 'product-details-enquire-button',
        requestQuoteButton: 'product-details-request-quote-button',
        longDescription: 'product-details-long-description',
        shortDescription: 'product-details-short-description',
        itemInformation: `product-details-item`,
      },
    },

    seedOrders: {
      header: {
        description: 'seed-order-type-description',
        verticalEllipsisMenu: 'seed-order-vertical-ellipsis',
        popoverMenu: 'seed-order-popover-menu',
        pickUpOrDeliveryOptions: 'seed-order-pick-up-or-delivery-options',
      },
      straightOrPremixed: {
        paddockHectares: 'paddock-hectares-input',
        paddockName: 'paddock-name-input',
        paddockPreferredDeliveryDate: 'paddock-date-input',
        addSeeds: 'add-seeds-button',
        showNotes: 'show-notes-toggle',
        addNotes: 'add-notes-text-area',
      },
      customMix: {},
      details: {
        addToOrderButton: 'seed-order-add-to-mix',
      },
      footer: {
        addAnotherPaddock: 'seed-order-add-paddock-button',
        addAnotherPaddockPremix: 'seed-order-add-paddock-premix',
        addAnotherPaddockCustom: 'seed-order-add-paddock-custom',
        calculatePrices: 'seed-order-calculate-prices-button',
        continue: 'seed-order-continue-button',
      },
    },

    cart: {
      list: {
        title: 'cart-title',
        clear: 'clear-cart-button',
        clearConfirm: 'clear-confirm-button',
      },
      summary: {
        pickUpButton: 'cart-summary-pickup-button',
        errorMessage: 'cart-summary-error-message',
        continueButton: 'cart-submit-order-button',
        requestCartQuote: 'request-cart-quote-button',
        addNotesButton: 'cart-add-notes-button',
        addInternalNotesButton: 'cart-add-internal-notes-button',
        notesField: 'cart-notes-field',
        internalNotesField: 'cart-internal-notes-field',
        customerRefField: 'cart-reference-field',
        deliveryCostNotice: 'cart-delivery-cost-notice',
        orderChargedMessage: 'cart-order-charged-message',
        urgentDeliveryWarning: 'cart-urgent-delivery-warning',
      },
      product: {
        card: 'cart-product-card',
      },
    },
    quotes: {
      sendOffer: {
        modal: 'quote-send-offer-modal',
        emailInput: 'quote-send-offer-email-input',
        emailDataId: (id: string) => `quote-send-offer-email-input-${id}`,
        note: 'quote-details-send-offer-notes',
        lock: 'quote-details-send-offer-lock',
        expiry: 'quote-details-send-offer-expiry',
        expiryError: 'quote-details-send-offer-expiry-error',
        freight: 'quote-details-send-offer-freight',
        location: 'quote-details-send-offer-location',
      },
      declineOffer: {
        radioGroup: 'quote-decline-radio',
        tooExpensive: 'quote-decline-too-expensive',
        noLongerNeedIt: 'quote-decline-no-longer-need-it',
        alreadyPurchased: 'quote-decline-already-purchased',
        other: 'quote-decline-other',
        reject: 'quote-decline-reject',
      },
      cancelOffer: {
        cancel: 'quote-cancel-cancel',
      },
      details: {
        quoteLocked: 'quote-details-quote-locked',
        addProductModal: {
          closeBtn: 'quote-details-add-product-modal-close-btn',
          listPaginationCounter: 'quote-details-add-product-modal-list-pagination-counter',
        },
      },
      filters: {
        quoteNumber: 'search-quote-no',
        expiringSoon: 'expiring-soon',
      },
      list: {
        noQuotes: 'no-quotes',
        loadMore: 'quotes-load-more-button',
        loadMoreError: 'quotes-load-more-error-msg',
        paginationCounter: 'quotes-pagination-counter',
      },
      card: {
        card: 'quote-card',
        title: 'quote-title',
        expiryDate: 'quote-expiry-date',
        totalCost: 'quote-total-cost',
        savings: 'quote-savings',
        status: 'quote-status',
        type: 'quote-type',
        numberOfItems: 'quote-number-of-items',
      },
      summary: {
        title: 'quote-summary-title',
        addNotesButton: 'quote-add-notes-button',
        notesField: 'quote-summary-notes-field',
        continueButton: 'quote-continue-button',
        pickUpButton: 'quote-pick-up-button',
        sendButton: 'quote-send-button',
        orderChargedMessage: 'quote-order-charged-message',
        freightUpdateMessage: 'quote-freight-update-message',
      },
    },
    ceLinkNoAccess: {
      logoutButton: 'ce-link-no-access-logout-button',
    },
    activities: {
      title: 'activities-title',
      error: 'activities-error',
      header: {
        newActivityButton: 'activities-header-new-activity-button',
      },
      activityItem: {
        component: 'activity-item',
        description: 'activity-item-description',
      },
      activitiesList: {
        component: 'activities-list',
        empty: 'activities-list-empty',
        loading: 'activities-list-loading',
      },
      createOrEditActivityModal: {
        title: 'create-or-edit-activity-modal-title',
        component: 'create-or-edit-activity-modal',
        error: 'create-or-edit-activity-modal-error',
        activityType: 'create-activity-modal-activity-type',
        activityStatus: 'create-or-edit-activity-modal-activity-status',
        subject: 'create-or-edit-activity-modal-subject',
        description: 'create-or-edit-activity-modal-description',
        scheduleDate: 'create-or-edit-activity-modal-schedule-date',
        owner: 'create-or-edit-activity-modal-owner',
        contactId: 'create-or-edit-activity-modal-contact-id',
        loading: 'create-or-edit-activity-modal-loading',
        cancelButton: 'create-or-edit-activity-modal-cancel-button',
        submitButton: 'create-or-edit-activity-modal-submit-button',
      },
      cancelActivityModal: {
        component: 'cancel-activity-modal',
        title: 'cancel-activity-modal-title',
        closeButton: 'cancel-activity-modal-close-button',
        submitButton: 'cancel-activity-modal-submit-button',
        loading: 'cancel-activity-modal-loading',
      },
      completeActivityModal: {
        component: 'complete-activity-modal',
        cancelButton: 'complete-activity-modal-cancel-button',
        completeButton: 'complete-activity-modal-complete-button',
        loading: 'complete-activity-modal-loading',
        campaignFormCard: {
          responseCodeLookupSelect: 'lookup-select-response-code-(optional)',
          responseCodeSelect: 'select-response-code-(optional)',
          reasonLookupSelect: 'lookup-select-reason',
          reasonSelect: 'select-reason',
          typeLookupSelect: 'lookup-select-type',
          typeSelect: 'select-type',
          competitorAutocomplete: 'competitor-autocomplete',
          competitorInput: 'competitor-input',
        },
      },
      statusBadge: {
        completed: 'status-badge-completed',
        canceled: 'status-badge-canceled',
        planned: 'status-badge-planned',
        unknown: 'status-badge-unknown',
      },
      activityDetails: {
        component: 'activity-details',
        error: 'activity-details-error',
        notFound: 'activity-details-not-found',
        description: 'activity-details-description',
        statusDropdown: 'activity-details-status-dropdown',
        editButton: 'activity-details-edit-button',
      },
    },
    orders: {
      filters: {
        orderNumber: 'search-order-no',
        pickUpOnlyCheckbox: 'pickup-only-checkbox',
        seedOnlyCheckbox: 'seed-only-checkbox',
        hideFarmlandsCardOrders: 'hide-farmlands-card-orders',
      },
      list: {
        noOrders: 'no-orders',
        loadMore: 'orders-load-more-button',
        loadMoreError: 'orders-load-more-error-msg',
        paginationCounter: 'orders-pagination-counter',
      },
      card: {
        card: 'order-card',
        title: 'order-title',
        customerRef: 'order-customer-reference',
        placedDate: 'order-placed-date',
        totalCost: 'order-total-cost',
        savings: 'order-savings',
        status: 'order-status',
        type: 'order-type',
        numberOfItems: 'number-of-items',
        cardVendor: 'order-card-vendor',
      },
      summary: {
        title: 'order-summary-title',
        customerInfo: 'order-summary-customerInfo',
        totalCost: 'order-summary-total-cost',
        gst: 'order-summary-gst-cost',
        discountCost: 'order-summary-discount-cost',
        deliveryFee: 'order-summary-delivery-fee',
      },
      details: {
        orderPlaced: 'od-order-placed',
        customerReference: 'od-customer-reference',
        totalCost: 'od-total-cost',
        totalSavings: 'od-total-savings',
        orderType: 'od-order-type',
        cardVendor: 'od-card-vendor',
        notes: 'od-notes',
        internalNotes: 'od-internal-notes',
        priceEditedBanner: 'od-price-edited-banner',
        shareOrder: 'shareOrder',
      },
      deliveryDetails: {
        deliveryName: 'od-delivery-name',
        address: 'od-delivery-address',
      },
      pickUpDetails: {
        orderOriginLabel: 'od-order-origin-label',
        storeName: 'od-store-name',
        address: 'od-store-address',
        phoneNumber: 'od-phone-number',
        emailAddress: 'od-email-ddress',
      },
    },

    checkout: {
      confirmationId: 'checkout-confirmation-id',
      contactModal: 'checkout-contact-modal',
      contactModalSubmit: 'checkout-modal-submit',
    },

    pickUpStore: {
      modal: 'pickup-store-modal',
      modalClose: 'pickup-store-modal--close',
      selectedStoreBranchName: 'selected-store-name',
      selectedStoreAddress: 'selected-store-address',
      selectedOpenHours: 'selected-store-open-hours',
      searchStoreField: 'search-pickup-store-field',
      storeListItem: 'pickup-store-list-item',
      saveButton: 'pickup-store-save-button',
    },

    pickupStoreSwitch: {
      modal: 'pickup-store-switch-modal',
      reviewItems: 'pickup-store-switch-modal-review-items',
      close: 'pickup-store-switch-modal--close',
    },

    requestQuote: {
      modal: 'request-a-quote-modal',
      bulkBanner: 'request-quote-bulk-banner',
      nameInput: 'request-quote-name-input',
      emailInput: 'request-quote-email-input',
      nameDisplay: 'request-quote-name-display',
      emailDisplay: 'request-quote-email-display',
      pickupOrDeliveryButton: 'request-quote-pickup-or-delivery-button',
      phoneInput: 'request-quote-phone-input',
      notesInput: 'request-quote-notes-input',
      submitButton: 'request-quote-submit-button',
      submitError: 'request-quote-api-error',
    },

    stock: {
      status: 'stock-status',
      urgentDeliveryBanner: 'urgent-delivery-banner',
      storeAvailabilitiesModal: 'store-availabities-modal',
      storeAvailabilitiesLink: 'store-availabilities-modal-link',
    },

    stockChangesAlert: {
      modal: 'stock-changes-alert-modal',
      reviewItems: 'stock-changes-alert-modal--review-items',
      close: 'stock-changes-alert-modal--close',
    },

    declarations: {
      openDeclarations: 'open-declarations-button',
      modal: 'declarations-modal',
      modalCloseButton: 'declarations-modal--close',
      downloadAllButton: 'declarations-download-all-button',
    },
    seed: {
      name: {
        input: 'seed-name-modal-input',
        closeButton: 'seed-name-modal-close-button',
      },
      notEnoughSeedsError: 'not-enough-seeds-error',
      seedSearch: {
        separateSeed: 'seed-order-separate-seed',
        addToMix: 'seed-order-add-to-mix',
      },
      address: {
        pickup: 'branch-pickup',
        seedStore: 'seed-store-pickup',
        delivery: 'delivery',
        error: 'no-fulfillment-selected-error',
      },
      paddock: {
        hectareInput: 'paddock-hectare-input',
        nameInput: 'paddock-name-input',
        dateInput: 'paddock-date-input',
        dateInputError: 'paddock-date-input-error',
        notEnoughPaddocksError: 'not-enough-paddocks-error',
      },
      product: {
        quantity: 'product-quantity-input',
        sowingRate: 'product-sowing-rate-input',
        treatment: 'product-treatment-input',
        notEnoughProductsError: 'not-enough-products-error',
        singleSeedInCustomSeed: 'single-seed-in-custom-seed',
        ponchoTreatmentCheckbox: 'product-poncho-treatment-checkbox',
      },
      note: {
        internalNote: 'seed-order-internal-note',
        externalNote: 'seed-order-external-note',
      },
      deliveryDetails: {
        component: 'seed-order__delivery-details',
        details: 'delivery-details__details',
      },
    },
  },

  components: {
    appNavigation: {
      mobileActionNavbar: {
        component: 'app-navigation__mobile-action-navbar',
        backBtn: 'mobile-action-navbar__back-btn',
        actions: 'mobile-action-navbar__actions',
      },
    },
    expandableContent: {
      collapseContent: 'expandable-content__collapse-content',
      toggleButton: 'expandable-content__toggle-button',
      toggleButtonIcon: 'expandable-content__toggle-button-icon',
    },
    formControls: {
      inputFormControl: {
        component: (id?: string) => id ?? 'form-controls__input-form-control',
        label: 'input-form-control-label',
        input: 'input-form-control-input',
        message: 'input-form-control-message',
      },
      selectFormControl: {
        component: (id?: string) => `${id ?? 'form-controls'}__select-form-control`,
        label: 'select-form-control-label',
        input: 'select-form-control-input',
        message: 'select-form-control-message',
      },
      label: {
        component: (id?: string) => `${id ?? 'form-controls'}__form-control-label`,
        optional: 'form-control-label__optional',
      },
      message: {
        component: (id?: string) => `${id ?? 'form-controls'}__form-control-message`,
        errorIcon: (id?: string) => `${id ?? 'form-control-message'}-error-icon`,
      },
    },
    forms: {
      unitPriceForm: {
        form: 'unit-price-form__form',
        unitPriceField: 'unit-price-form__unit-price-field',
        reasonField: 'unit-price-form__reason-field',
        commentsField: 'unit-price-form__comments-field',
        revertChangesBtn: 'unit-price-form__revert-changes-btn',
      },
    },
    layout: {
      header: {
        component: 'layout__header',
      },
      nav: {
        desktopDrawer: 'nav__desktop-drawer',
        bottomNavigation: 'nav__bottom-navigation',
      },
    },
    quicklink: { agreements: 'quicklink-agreements', quotes: 'quicklink-quotes' },
    address: {
      forPickup: 'address-pickup-button',
      forDelivery: 'address-delivery-button',
      instructionsField: 'drawdown-address-instructions',
      datePickerInput: 'drawdown-address-preferred-delivery-time',
      dairySupplyInput: 'drawdown-daddress-dairy-supply-input',
      addressName: 'drawdown-address-name-input',
      saveAddress: 'drawdown-address-save',
      deliveryDateErrorMessage: 'drawdown-address-deliveryDateErrorMessage',
      editButton: 'drawdown-address-edit-button',
      searchInput: 'drawdown-address-search-input',
      knownAddressItem: 'drawdown-address-known-address-item',
      // Pass in `fulladdress` property
      knownAddressDataId: (id: string) => `drawdown-address-known-address-${id}`,
      searchAddressItem: 'drawdown-search-address-item',
      // Pass in `UniqueId` property
      searchAddressDataId: (id: string) => `drawdown-address-search-address-${id}`,
      urgentDelivery: 'address-urgent-delivery',
    },
    filters: {
      searchProductField: 'product-search-field',
      searchProductFieldMenuItem: 'product-search-menuItem',
      categoryDropdownMenuItem: 'product-filter-category-menuitem',
      categoryDropdownMenuItemDataId: (id: string) => `product-filter-category-menuitem-${id}`,
      timeSelector: 'product-filter-time',
      dateSelector: 'product-filter-daterange',
      fromDatePicker: 'product-filter-daterange-from',
      toDatePicker: 'product-filter-daterange-to',
      fromPrice: 'product-filter-pricerange-from',
      toPrice: 'product-filter-pricerange-to',
      categoryChip: 'product-category-chip-menu',
      dateRangeChip: 'product-date-range-chip-menu',
      priceRangeChip: 'product-price-range-chip-menu',
      viewResults: 'product-chip-menu-view-results',
      inStockCheckbox: 'product-filter-in-stock-checkbox',
      searchProductIconBtn: 'product-search-icon-btn',
    },

    modal: {
      closeBtn: (id = 'modal') => `${id}--close`,
    },

    accountSwitch: {
      selector: 'account-selector',
      switcher: 'account-switcher',
      chip: 'account-chip',
      menuItem: 'account-switcher-menuItem',
      message: 'account-switch-message',
      searchBar: 'account-switcher-search-bar',
      alertModal: 'account-alert-modal',
      switchAccountButton: 'account-switch-account-button',
      viewCart: 'account-switch-view-cart',
      recentViews: 'account-recent-views',
    },

    cartHeader: {
      cartShortcutButton: 'header-cart-shortcut',
      inboxButton: 'header-inbox-shortcut',
      pickupDeliveryShortcutButton: 'header-pickup-shortcut',
    },

    statusHeader: {
      container: 'status-header',
      title: 'status-header-title',
      subtitle: 'status-header-subtitle',
      statusBadge: 'status-header-status-badge',
      text: 'status-header-text',
    },

    helperMessage: {
      container: 'product-help-message',
    },

    pagination: {
      number: 'pagination-number',
      clearFilterButton: 'clear-filter-button',
      sortingDropdown: 'product-sort-type',
    },

    counter: {
      amount: 'add-quantity-amount',
      removeButton: 'add-quantity-reduce',
      addButton: 'add-quantity-add',
    },
    contactCard: {
      card: 'checkout-contact-card',
      title: 'contact-card-title',
      phone: 'contact-card-phone',
      storeName: 'contact-card-store-name',
      storeAddress: 'contact-card-store-address',
      storeDirection: 'contact-card-store-direction',
    },
    bottomDrawer: {
      account: 'bottom-drawer-account',
      agreements: 'bottom-drawer-agreement',
      pastProducts: 'bottom-drawer-past-products',
      declarations: 'bottom-drawer-declarations',
      quotes: 'bottom-drawer-quotes',
      orders: 'bottom-drawer-orders',
      logout: 'bottom-drawer-logout',
      home: 'bottom-drawer-home',
      seed: 'bottom-drawer-seed',
    },
    sideDrawer: {
      agreements: 'drawer-agreement',
      pastProducts: 'drawer-past-products',
      seed: 'drawer-seed',
      quotes: 'drawer-quotes',
      orders: 'drawer-orders',
      activities: 'drawer-activities',
      home: 'drawer-home',
    },
    quickActions: {
      productShortcut: 'quickActions-productShortcut',
      agreementShortcut: 'quickActions-agreementShortcut',
    },
    stepper: {
      step: 'stepper-step-of-steps',
      back: 'stepper-back-button',
    },
    treeView: {
      treeView: 'tree-view',
      nodeTestId: 'tree-view-node',
      nodeDataId: (pathName: string) => 'tree-node-' + pathName.replaceAll('/', '.'),
      downloadFileButton: 'download-file-button',
    },

    breadcrumbs: {
      container: 'breadcrumbs-container',
      item: (itemName: string) => `breadcrumb-item-${itemName}`,
    },

    backButton: {
      productDetails: 'back-button-product-details',
      activityDetails: 'back-button-activity-details',
    },

    loader: {
      searchProduct: 'loader-search-product',
    },

    // Contact Info Inputs
    contactInfo: {
      nameInput: 'contact-name-input',
      emailInput: 'contact-email-input',
      phoneInput: 'contact-phone-input',
      refInput: 'contact-reference-input',
    },

    searchWidget: {
      searchBar: 'search-widget-search-bar',
      radioButtonGroup: 'search-widget-radio-button-group',
      radioButtonGroupDataId: (id: string) => `search-widget-radio-button-group-${id}`,
      searchIcon: 'search-widget-search-icon',
    },
    toastify: {
      closeBtn: 'toastify__close-btn',
    },
  },
};

export default TestIds;
